import {
  Card,
  Chart,
  FuelPump,
  Guage,
  Notification,
  MapB,
  MessageBubbleStack,
  ShieldCircle,
  Wrench,
  Document,
  GraphArrow,
  Table,
  Monitor,
  Person,
  Geofence,
  PersonGroup,
  VehicleTrailer,
  VehicleTruck,
  BuildingOffice,
  DocumentDollar,
  BackslashBrackets,
  ShieldLock,
  Loads,
  ShoppingBag,
  LocationPinArrows,
  Mobile,
  Clock,
  AISparkle,
  PhxTooltipIntent,
  Whistle,
  DocumentGear,
  Devices,
  DocumentPencil,
} from 'phx-core';

import { AdminFeatureFlagService } from '@services/feature-flags/admin';
import { DriverAppFeatureFlagService } from '@services/feature-flags/driver-app';
import { EnterprisePlatformFeatureFlagService } from '@services/feature-flags/enterprise-platform';
import { FacilityMonitoringFeatureFlagService } from '@services/feature-flags/facility-monitoring';
import { FleetCardsFeatureFlagService } from '@services/feature-flags/fleet-cards';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';
import { MotiveGptFeatureFlagService } from '@services/feature-flags/motive-gpt';
import { FleetViewFeatureFlagService } from '@services/feature-flags/fleet-view';
import { ComplianceFeatureFlagService } from '@services/feature-flags/compliance';
import { SmartLoadBoardFeatureFlagService } from '@services/feature-flags/smart-load-board';
import { CoachingFeatureFlagService } from '@services/feature-flags/coaching';
import { WorkforceHubFeatureFlagService } from '@services/feature-flags/workforce-hub';
import { GroupLevelSettingsConfigService } from '@services/dynamic-configs/group-level-settings-config';
import { DeviceHubFeatureFlagService } from '@services/feature-flags/device-hub';
import { EntCustomReportingDynamicConfigService } from '@services/dynamic-configs/ent-custom-reporting-enabled';
import { FuelFeatureFlagService } from '@services/feature-flags/fuel';

import { Session } from '@services/session';

import { getShopUrl } from '@lib/utils';
import { hasCoachingAccess } from '@lib/coaching/coaching.utils';
import { inDemoMode, inSupportMode, isRemoteLoginSession } from '@lib/remote-login';
import { isEuDomain } from '@lib/environments.const';
import { ECOMM_ELIGIBLE_ENTITIES } from '@app/admin/shop/ecomm-eligible-entities';

export interface IStatsigFlag {
  fleetcardsFeatureFlagService?: FleetCardsFeatureFlagService;
  fgFeatureFlagService?: FleetGrowthFeatureFlagService;
  fvFeatureFlagService?: FleetViewFeatureFlagService;
  driverAppFeatureFlagService?: DriverAppFeatureFlagService;
  facilityMonitoringFeatureFlagService?: FacilityMonitoringFeatureFlagService;
  adminFeatureFlagService?: AdminFeatureFlagService;
  motiveGptFeatureFlagService?: MotiveGptFeatureFlagService;
  enterprisePlatformFeatureFlagService?: EnterprisePlatformFeatureFlagService;
  complianceFeatureFlagService?: ComplianceFeatureFlagService;
  smartLoadBoardFeatureFlagService?: SmartLoadBoardFeatureFlagService;
  coachingFeatureFlagService?: CoachingFeatureFlagService;
  workforceHubFeatureFlagService?: WorkforceHubFeatureFlagService;
  glsConfig?: GroupLevelSettingsConfigService,
  deviceHubFeatureFlagService?: DeviceHubFeatureFlagService;
  customReportingDynamicConfigService?: EntCustomReportingDynamicConfigService;
  fuelFeatureFlagService?: FuelFeatureFlagService;
}

export interface INavItem {
  label: string;
  link: any;
  name: string;
  shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag) => boolean;
  group?: ADMIN_NAV_GROUPS;
  count?: number;
  isAlphaTag?: boolean;
  isBetaTag?: boolean;
  isNewTag?: boolean;
  showNotificationDot?: boolean;
  openInNewTab?: boolean;
  params?: { [key: string]: any };
  isOutsideLink?: boolean;
  dashboardDividerGroup?: number;
}

export interface IButtonConfig {
  text: string;
  onClick: () => void
}

export interface ISidebarPopoverConfig {
  key: string;
  header: string;
  content: string;
  subContent?: string;
  intent: PhxTooltipIntent;
  primaryBtn?: IButtonConfig;
  secondaryBtn?: IButtonConfig;
  onDismiss?: () => void;
}

const SHARED_COMPLIANCE_PROPERTIES = {
  label: $localize`Compliance`,
  link: '/compliance',
  name: 'compliance',
};

export const DASHBOARD_NAV_ITEMS: INavItem[] = [
  {
    label: $localize`Fleet View`,
    link: '/fleetview',
    name: 'fleetView',
    shouldNavItemShow: (session: Session) => session.isFleetViewAllowed(),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Fleet Hub`,
    link: '/fleet-hub',
    name: 'fleetHub',
    isBetaTag: true,
    shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag) => {
      const shouldAllow = (session.isFleetViewAllowed() && statsigFlags?.fvFeatureFlagService?.isFleetViewV2Enabled()) ||
        inSupportMode() ||
        inDemoMode();
      return shouldAllow;
    },
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Overview`,
    link: '/overview',
    name: 'overview',
    shouldNavItemShow: (session: Session) => session.isOldOverviewEnabled(),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Logs`,
    link: '/logs',
    name: 'logs',
    shouldNavItemShow: (session: Session) => !session.isComplianceHubEnabled() &&
      (session.isDriver() || session.userPermissions.isLogsReadAllowed()),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Trips`,
    link: '/trips',
    name: 'trips',
    shouldNavItemShow: (session: Session) => !session.isComplianceHubEnabled() &&
      (session.isDispatcher() && session.userPermissions.isTripsManageAllowed()),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Safety`,
    link: '/safety',
    name: 'safety',
    shouldNavItemShow: (session: Session) => session.isDispatcher() &&
      (session.userPermissions.isDriverPerformanceManageAllowed() || session.isAdmin()),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Safety`,
    link: '/driver-safety',
    name: 'safety',
    shouldNavItemShow: (session: Session): boolean => (
      session.isDriver() &&
      session.hasCompanyDriverPerformanceEnabled() &&
      session.hasCompanyDriverCoachingEnabled()
    ),
    openInNewTab: true,
    dashboardDividerGroup: 1,
  },
  {
    ...SHARED_COMPLIANCE_PROPERTIES,
    shouldNavItemShow: (session: Session) => session.isComplianceHubEnabled(),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Fuel`,
    link: '/fuel',
    name: 'fuel',
    shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag): boolean => (
      session.isDispatcher() && statsigFlags?.fuelFeatureFlagService?.isFuelShowTabSidebarEnabled() &&
      (session.userPermissions.isIftaReadAllowed()
        || session.userPermissions.isIdlingReadAllowed()
        || session.userPermissions.isFuelInsightsAllowed()
        || session.isAdmin())),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Cards`,
    link: '/fleet-cards',
    name: 'fleetCards',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => 
      statsigFlags?.fleetcardsFeatureFlagService.isFleetCardShowCardsTabSidebarEnabled() &&
      (session.isFleetCardDashboardNavItemEnabled() && !session.isMexicanFleet()),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Maintenance`,
    link: '/maintenance',
    name: 'maintenance',
    shouldNavItemShow: (session: Session) => session.isMaintenanceReminderEnabled() && !session.isDriver(),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Maintenance`,
    link: '/maintenance',
    name: 'maintenance',
    shouldNavItemShow: (session: Session) => {
      return (
        (session.userPermissions.isInspectionReportsManageAllowed() && !session.isMaintenanceReminderEnabled()) ||
        session.isDriver()
      );
    },
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Workforce`,
    link: '/workforce',
    name: 'workforce',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => (
      !session.isDriver() &&
      session.userPermissions.isWorkforceManageAllowed()
    ),
    dashboardDividerGroup: 1,
  },
  {
    label: $localize`Dispatch`,
    link: '/dispatch',
    name: 'dispatch',
    dashboardDividerGroup: 1,
    shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag) =>
      statsigFlags?.fgFeatureFlagService.isDispatchDashboardNavItemEnabled(),
  },
  {
    label: $localize`Coaching`,
    link: '/coaching',
    name: 'coaching',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => (
      hasCoachingAccess(session, statsigFlags?.coachingFeatureFlagService)
    ),
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Messages`,
    link: '/messages',
    name: 'message',
    shouldNavItemShow: (session: Session) => session.isDispatcher() && session.userPermissions.isMessageManageAllowed(),
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Documents`,
    link: '/documents',
    name: 'documents',
    shouldNavItemShow: (session: Session) => session.userPermissions.isDocumentManageAllowed(),
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Smart Load Board`,
    link: '/load-board',
    name: 'loadBoard',
    shouldNavItemShow: (session: Session) => session.isSmartLoadBoardFeatureAllowed(),
    openInNewTab: true,
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`MotiveGPT`,
    link: '/motivegpt',
    name: 'motiveGpt',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => (
      statsigFlags?.motiveGptFeatureFlagService.isGenAiPocEnabled()
    ),
    isBetaTag: true,
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Analytics`,
    link: '/analytics',
    name: 'analytics',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) =>
      session.isDispatcher() && session.isCompanyPaidPlan() && statsigFlags?.customReportingDynamicConfigService.isAnalyticsHubEnabled() &&
        session.isMotiveAnalyticsReadAllowed(),
    isNewTag: true,
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Reports`,
    link: '/reports',
    name: 'reportsNew',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) =>
      session.isDispatcher() &&
      (
        session.userPermissions.isReportReadAllowed() ||
        !statsigFlags?.enterprisePlatformFeatureFlagService?.isReportsPermissionEnforcementEnabled()
      ) && statsigFlags?.customReportingDynamicConfigService.isAnalyticsHubEnabled(),
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Reports`,
    link: '/reports',
    name: 'reports',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) =>
      session.isDispatcher() &&
      (
        session.userPermissions.isReportReadAllowed() ||
        !statsigFlags?.enterprisePlatformFeatureFlagService?.isReportsPermissionEnforcementEnabled()
      ) && !statsigFlags?.customReportingDynamicConfigService.isAnalyticsHubEnabled(),
    dashboardDividerGroup: 2,
  },
  {
    label: 'Devices',
    link: '/devices',
    name: 'deviceHub',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => (
      statsigFlags?.deviceHubFeatureFlagService.isDeviceHubEnabled()
    ),
    isNewTag: true,
    dashboardDividerGroup: 2,
  },
  {
    label: $localize`Marketplace`,
    link: '/app-marketplace',
    name: 'marketplace',
    shouldNavItemShow: (session: Session) =>
      session.isDispatcher() && session.userPermissions.isMarketplaceReadAllowed(),
    dashboardDividerGroup: 2,
  },
  {
    label: 'SSE',
    link: '/marketplace',
    name: 'marketplace',
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => (
      statsigFlags?.fgFeatureFlagService?.isMarketplaceExpansionEnabled()
    ),
    isNewTag: false,
    dashboardDividerGroup: 2,
  },
];

export enum ADMIN_NAV_GROUPS {
  ORGANIZATION = 'organization',
  PRODUCTS = 'products',
  PLATFORM = 'platform',
}

export const ADMIN_NAV_GROUPS_MAP = {
  [ADMIN_NAV_GROUPS.ORGANIZATION]: $localize`Organization`,
  [ADMIN_NAV_GROUPS.PRODUCTS]: $localize`Products`,
  [ADMIN_NAV_GROUPS.PLATFORM]: $localize`Platform`,
};

export const ADMIN_NAV_ITEMS: INavItem[] = [
  {
    label: $localize`Company`,
    link: '/admin/company',
    name: 'company',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session) => session.userPermissions.isCompanyManageAllowed(),
  },
  {
    label: $localize`Fleet Users`,
    link: '/admin/users',
    name: 'fleetUsers',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session) => session.userPermissions.isFleetUsersManageAllowed(),
  },
  {
    label: $localize`Drivers`,
    link: '/admin/drivers',
    name: 'drivers',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => (
      session.userPermissions.isDriversManageAllowed() || session.userPermissions.isFaceMatchManageAllowed()
    ),
  },
  {
    label: $localize`Vehicles`,
    link: '/admin/vehicles',
    name: 'vehicles',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session) => session.userPermissions.isVehiclesManageAllowed(),
  },
  {
    label: $localize`Assets`,
    link: '/admin/assets',
    name: 'assets',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session) => session.userPermissions.isAssetsManageAllowed(),
  },
  {
    label: $localize`Geofences`,
    link: '/admin/geofences',
    name: 'geofences',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session) => session.userPermissions.isGeofenceManageAllowed(),
  },
  {
    label: $localize`Groups`,
    link: '/admin/groups',
    name: 'groups',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session) => session.userPermissions.isGroupsManageAllowed(),
  },
  {
    label: $localize`Profiles`,
    link: '/admin/profiles',
    name: 'profiles',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => (
      session.userPermissions.isCompanyManageAllowed() && statsigFlags?.glsConfig.isProfileManagementEnabled()
    ),
  },
  {
    label: $localize`Billing`,
    link: '/admin/billing',
    name: 'billing',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => (
      statsigFlags?.fgFeatureFlagService.isAdminBillingManagementEnabled()
      ),
    isNewTag: true,
  },
  {
    label: $localize`Shop`,
    link: '/admin/shop',
    name: 'shop',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => {
      return ECOMM_ELIGIBLE_ENTITIES.EXPERIMENT.includes(session.getEcommEligibility()) &&
          statsigFlags.fgFeatureFlagService.isContactSalesExperimentEnabled() &&
          statsigFlags.fgFeatureFlagService.isCompanyWiseShopPermissionEnabled() &&
          session.userPermissions.isShopManageAllowed() &&
          !isRemoteLoginSession();
    },
    isNewTag: true,
  },
  {
    label: $localize`Shop`,
    link: getShopUrl(),
    name: 'shop_utm',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => {
      return ECOMM_ELIGIBLE_ENTITIES.EXPERIMENT.includes(session.getEcommEligibility()) &&
          !statsigFlags.fgFeatureFlagService.isContactSalesExperimentEnabled() &&
          statsigFlags.fgFeatureFlagService.isCompanyWiseShopPermissionEnabled() &&
          session.userPermissions.isShopManageAllowed() &&
          !isRemoteLoginSession();
    },
    isOutsideLink: true,
    openInNewTab: false,
  },
  {
    label: $localize`Shop`,
    link: '',
    name: 'shop_exp',
    group: ADMIN_NAV_GROUPS.ORGANIZATION,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => {
      return ECOMM_ELIGIBLE_ENTITIES.NO_EXPERIMENT.includes(session.getEcommEligibility()) &&
          session.userPermissions.isShopManageAllowed() &&
          statsigFlags.fgFeatureFlagService.isCompanyWiseShopPermissionEnabled() &&
          !isRemoteLoginSession();
    },
    isNewTag: true,
  },
  {
    label: $localize`Fleet View`,
    link: '/admin/fleetview',
    name: 'fleetView',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session) => session.userPermissions.isCompanyManageAllowed(),
  },
  {
    label: $localize`Safety`,
    link: '/admin/safety',
    name: 'safety',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => (
      session.userPermissions.isDriverPerformanceManageAllowed() &&
      session.userPermissions.isCompanyManageAllowed()
    ),
  },
  {
    label: $localize`Compliance`,
    link: '/admin/compliance',
    name: 'compliance',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session) => session.userPermissions.isCompanyManageAllowed(),
  },
  {
    label: $localize`Fuel`,
    link: '/admin/fuel',
    name: 'fuel',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag): boolean => session.userPermissions.isCompanyManageAllowed()
      && statsigFlags?.fuelFeatureFlagService?.isFuelShowTabSidebarEnabled(),
  },
  {
    label: $localize`Cards`,
    link: '/admin/fleet-cards',
    name: 'fleetCards',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => 
      statsigFlags?.fleetcardsFeatureFlagService.isFleetCardShowCardsTabSidebarEnabled() &&
      (session.isFleetCardDashboardNavItemEnabled() && !session.isMexicanFleet()),
  },
  {
    label: $localize`Maintenance`,
    link: '/admin/maintenance',
    name: 'maintenance',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session) =>
      ((session.userPermissions.isVehiclesManageAllowed() || session.userPermissions.isAssetsManageAllowed()) &&
        session.userPermissions.isInspectionReportsManageAllowed()) ||
      (session.userPermissions.isCompanyManageAllowed() && session.isCompanyPaidPlan()),
  },
  {
    label: $localize`Workforce`,
    link: '/admin/workforce',
    name: 'workforce',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => (
      session.userPermissions.isCompanyManageAllowed() &&
      session.userPermissions.isTimecardsSettingsManageAllowed()
    ),
  },
  {
    label: $localize`Dispatch`,
    link: '/admin/dispatch',
    name: 'dispatch',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag) =>
      statsigFlags?.fgFeatureFlagService.isDispatchDashboardNavItemEnabled(),
  },
  {
    label: $localize`Coaching`,
    link: '/admin/coaching',
    name: 'coachingAdmin',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag) =>
      !statsigFlags?.coachingFeatureFlagService.isComplianceCoachingSettingsRevertEnabled() &&
      session.isDispatcher() &&
      session.userPermissions.isCompanyManageAllowed(),
  },
  {
    label: $localize`Messaging`,
    link: '/admin/messaging-teams',
    name: 'messagingTeams',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session) => session.userPermissions.isManageMessagingTeamsAllowed() ||
      session.userPermissions.isCompanyManageAllowed(),
  },
  {
    label: $localize`Smart Load Board`,
    link: '/admin/loadboard-settings',
    name: 'loadBoardSettings',
    group: ADMIN_NAV_GROUPS.PRODUCTS,
    shouldNavItemShow: (session: Session, statsigFlags?: IStatsigFlag) => session.userPermissions.isCompanyManageAllowed() &&
      !session.isSLBOptOut() &&
      !statsigFlags?.smartLoadBoardFeatureFlagService.isSlbAccessShutdownEnabled(),
  },
  {
    label: $localize`Security and Data`,
    link: '/admin/security-data',
    name: 'security',
    group: ADMIN_NAV_GROUPS.PLATFORM,
    shouldNavItemShow: (session: Session) => session.userPermissions.isCompanyManageAllowed(),
  },
  {
    label: $localize`Alerts`,
    link: '/admin/alerts-platform',
    name: 'alerts',
    group: ADMIN_NAV_GROUPS.PLATFORM,
    shouldNavItemShow: (session: Session) => session.userPermissions.isAlertsManageAllowed(),
  },
  {
    label: $localize`Developers`,
    link: '/admin/developers',
    name: 'developers',
    group: ADMIN_NAV_GROUPS.PLATFORM,
    shouldNavItemShow: (session: Session) => session.userPermissions.isDeveloperAccessManageAllowed(),
  },
  {
    label: $localize`Driver App`,
    link: '/admin/driver-app',
    name: 'driverApp',
    group: ADMIN_NAV_GROUPS.PLATFORM,
    shouldNavItemShow: (session: Session, statsigFlags: IStatsigFlag) => (
      (
        session.isCompanyBestPlan() &&
        statsigFlags?.driverAppFeatureFlagService?.isDriverHubEnabled() &&
        session.userPermissions.isDriverHubAllowed()
      ) || session.userPermissions.isDriverWalkthroughAllowed()
    ),
  },
  {
    label: $localize`Audit Log`,
    link: '/admin/audit-log',
    name: 'auditLog',
    group: ADMIN_NAV_GROUPS.PLATFORM,
    shouldNavItemShow: (session: Session): boolean => session.userPermissions.isAuditLogsManageAllowed(),
  },
];

export const PHX_ICON_NAMES_MAP = {
  fleetView: MapB,
  fleetHub: MapB,
  overview: Guage,
  compliance: Chart,
  logs: Chart,
  trips: '',
  facilityMonitoring: '',
  safety: ShieldCircle,
  maintenance: Wrench,
  utlization: '',
  fuel: FuelPump,
  fleetCards: Card,
  message: MessageBubbleStack,
  documents: Document,
  facilities: '',
  reportsNew: Table,
  reports: GraphArrow,
  analytics: GraphArrow,
  drivers: Person,
  fleetManagers: '',
  fleetUsers: Monitor,
  vehicles: VehicleTruck,
  assets: VehicleTrailer,
  groups: PersonGroup,
  geofences: Geofence,
  company: BuildingOffice,
  devices: '',
  profiles: DocumentGear,
  billing: DocumentDollar,
  alerts: Notification,
  developers: BackslashBrackets,
  admin: '',
  avatar: '',
  dashboard: '',
  back: '',
  security: ShieldLock,
  loadBoardSettings: Loads,
  loadBoard: Loads,
  shop: ShoppingBag,
  shop_exp: ShoppingBag,
  shop_utm: ShoppingBag,
  marketplace: ShoppingBag,
  dispatch: LocationPinArrows,
  driverApp: Mobile,
  timecards: Clock,
  messagingTeams: MessageBubbleStack,
  motiveGpt: AISparkle,
  auditLog: DocumentPencil,
  coaching: Whistle,
  coachingAdmin: Whistle,
  workforce: Person,
  deviceHub: Devices,
};

export const FLEET_CARDS_NOTIFICATION_POPOVER_TEXT_CONFIG = {
  cardsReceived: {
    header: $localize`🎉  Your cards have arrived! `,
    content: (spendLimit: string, numberOfCards: string) => {
      if (Number(numberOfCards) > 1) {
        return $localize`You’ve been approved for a ${spendLimit} spend limit and your ${numberOfCards} cards are ready to be activated. `;
      } else {
        return $localize`You’ve been approved for a ${spendLimit} spend limit and your card is ready to be activated. `;
      }
    },
  },
  cardsShipped: {
    header: $localize`🎉 Congratulations!`,
    content: (spendLimit: string, numberOfCards: string) => {
      if (Number(numberOfCards) > 1) {
        // tslint:disable-next-line: max-line-length
        return $localize`You’ve been approved for a ${spendLimit} spend limit and your ${numberOfCards} cards have been shipped. `;
      } else {
        return $localize`You’ve been approved for a ${spendLimit} spend limit and your card has been shipped. `;
      }
    },
  },
  pastApprovedCustomers: {
    header: (numberOfCards: string): string => {
      if (Number(numberOfCards) > 1) {
        return $localize`🎉  You have ${numberOfCards} cards ready to be activated! `;
      } else {
        return $localize`🎉  You have ${numberOfCards} card ready to be activated! `;
      }
    },
    content: $localize`Complete this step to get full visibility into your fleet’s fuel spend and consumption.`,
  },
};


export const FLEET_CARDS_NOTIFICATION_POPOVER_EVENTS = {
  activateNowBtnClicked: 'Fleetcard - Activation Reminder - Click - Activate Cards',
  exploreBenefitsBtnClicked: 'Fleetcard - Activation Reminder - Click - Explore Benefits',
  dismissClicked: 'Fleetcard - Activation Reminder - Click - Dismiss',
  viewEvent: 'Fleetcard - Activation Reminder - View',
  activateCardEvent: 'Fleetcard - Activation Reminder - Event - Activate Card',
};

export const ACCOUNT_NAV_ITEM: INavItem[] = [
  {
    label: $localize`Account & Security`,
    link: '/settings/account',
    name: 'account',
    shouldNavItemShow: (session: Session) => true,
  },
  {
    label: $localize`Log Settings`,
    link: '/settings/log',
    name: 'logSettings',
    shouldNavItemShow: (session: Session) => session.isDriver(),
  },
  {
    label: $localize`Print Settings`,
    link: '/settings/print',
    name: 'printSettings',
    shouldNavItemShow: (session: Session) => true,
  },
];
